import cx from 'classnames';
import {FormikProps, FormikProvider, useFormik} from 'formik';
import {forEach, isEmpty, isNumber, set} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {settingsApi} from 'api';
import {
    AssignmentSelectorsFormik, Button, Card, DataWrapper, InputFormik, MultiInputFormik,
    PredefinedNotifierType, SelectFormik, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {Group, SessionScreenshot, SessionScreenshotSubject} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.IN_SESSION.SCREENSHOTS_MANAGEMENT;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const ACCOUNT_ASSIGNMENT = 'account';

type FormFields = {
    screenshots: SessionScreenshot[];
    assignment: {
        groupId: string
    }
}

type FormTemplateProps = {
    maxScreenshots: number;
    groups: Group[];
    guestEnabled?: boolean;
} & FormikProps<FormFields>;

const FORM_FIELD_KEY = {
    SCREENSHOTS: 'screenshots',
    NAME: 'name',
    SUBJECT: 'subject',
    REQUIRED: 'required',
    ROLE: 'role'
};

const FormTemplate: React.FC<FormTemplateProps> = ({
    maxScreenshots, groups, guestEnabled,
    values: formValues, initialValues, errors, submitForm,
}: FormTemplateProps) => {
    const {t} = useTranslation();
    const isItemValid = useCallback((item) => !isEmpty(item?.name) && !isEmpty(item?.subject), []);
    const isMaxScreenshotsExceeded = useMemo(() => isNumber(maxScreenshots) && formValues.screenshots?.length > maxScreenshots,
        [formValues.screenshots?.length, maxScreenshots]);

    const subjectOptions = useMemo(() => [
        {
            id: SessionScreenshotSubject.LocalScreen,
            name: t(T_KEYS.SUBJECTS.LOCAL_SCREEN)
        },
        {
            id: SessionScreenshotSubject.ParticipantVideo,
            name: t(T_KEYS.SUBJECTS.PARTICIPANT_VIDEO)
        }
    ], [t]);

    const roleOptions = useMemo(() => [
        {
            id: InviteeParticipantRole.Client,
            name: t(COMMON_T_KEYS.SESSION.LABELS.CLIENT)
        },
        {
            id: InviteeParticipantRole.Guest,
            name: t(COMMON_T_KEYS.SESSION.LABELS.GUEST)
        }
    ], [t]);

    const saveDisabled = useMemo(() =>
        dataUtils.isEqualValues(initialValues.screenshots, formValues.screenshots, true) || !isEmpty(errors),
    [formValues.screenshots, initialValues.screenshots, errors]);

    return (
        <Card className='tw-px-10 -sm:tw-px-5'>
            <div className='tw-py-4 tw-mb-4'>
                <div className='tw-mb-8'>
                    <span className='tw-ts-h5-bold'>{t(T_KEYS.TITLE)}</span>
                    <span className={cx('tw-ml-1', {'tw-text-alert': isMaxScreenshotsExceeded})}>
                        {`(${formValues.screenshots?.length} `}
                        {maxScreenshots && `${t(COMMON_T_KEYS.ALL.OF)} ${maxScreenshots} `}
                        {`${t(T_KEYS.LOADED)})`}
                    </span>
                </div>
                {isMaxScreenshotsExceeded && <div className='tw-text-alert'>{t(T_KEYS.ERRORS.MAX_SCREENSHOTS)}</div>}
                <AssignmentSelectorsFormik name='assignment' groups={groups}>
                    {({renderGroup}) => (
                        <div className='tw-mr-12 tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-3'>
                            {renderGroup()}
                        </div>
                    )}
                </AssignmentSelectorsFormik>
            </div>
            <div className='tw-mb-4 tw-flex tw-flex-col tw-items-stretch'>
                <MultiInputFormik
                    name='screenshots'
                    itemsPath={FORM_FIELD_KEY.SCREENSHOTS}
                    defaultValue={{
                        name: '', subject: null, required: false,
                        role: InviteeParticipantRole.Client
                    } as Omit<SessionScreenshot, 'received'>}
                    values={formValues}
                    maxItems={maxScreenshots}
                    isValid={isItemValid}
                >
                    {({index, item, last, path, renderActions}) => (
                        <div key={index} className='tw-flex tw-items-baseline -md:tw-mb-3'>
                            <div className='tw-flex tw-items-baseline -md:tw-flex-col tw-w-full'>
                                <div className='tw-w-full tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-my-2'>
                                    <InputFormik
                                        validate={!last}
                                        name={`${path}.${FORM_FIELD_KEY.NAME}`}
                                        label={t(last ? T_KEYS.NEW_NAME : T_KEYS.NAME)}
                                        maxLength={100}
                                        className='tw-flex-1 md:tw-mr-2 -md:tw-mt-controls'
                                    />
                                    <SelectFormik
                                        name={`${path}.${FORM_FIELD_KEY.SUBJECT}`}
                                        placeholder={t(T_KEYS.SUBJECT)}
                                        options={subjectOptions}
                                        valueAs='id'
                                        className={cx('tw-flex-grow -md:tw-mt-controls', {
                                            'tw-col-span-2': !(guestEnabled && item?.subject === SessionScreenshotSubject.ParticipantVideo)
                                        })}
                                    />
                                    {guestEnabled && item?.subject === SessionScreenshotSubject.ParticipantVideo && (
                                        <SelectFormik
                                            className='tw-flex-grow md:tw-ml-4 -md:tw-mt-controls'
                                            name={`${path}.${FORM_FIELD_KEY.ROLE}`}
                                            placeholder={t(T_KEYS.ROLE)}
                                            options={roleOptions}
                                            valueAs='id'
                                        />
                                    )}
                                    <ToggleFormik
                                        className='md:tw-ml-4 -md:tw-mt-controls'
                                        name={`${path}.${FORM_FIELD_KEY.REQUIRED}`}
                                        label={t(COMMON_T_KEYS.ALL.REQUIRED)}
                                        labelPlacement='right'
                                    />
                                </div>
                            </div>
                            <div className='tw-w-10 tw-ml-3 tw-self-center tw-shrink-0 -md:tw-self-start -md:tw-mt-9 tw-mt-3'>
                                {renderActions({index, item})}
                            </div>
                        </div>
                    )}
                </MultiInputFormik>
            </div>
            <div className='tw-my-4'>
                <Button color='primary' disabled={saveDisabled} onClick={submitForm}>
                    {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                </Button>
            </div>
            <UnsavedChangesLeavePrompt values={formValues.screenshots} initialValues={initialValues.screenshots} />
        </Card>
    );
};

type Props = {
    groups: Group[];
    guestEnabled?: boolean;
}

export const ScreenshotsManagement: React.FC<Props> = ({groups, guestEnabled}) => {
    const {t} = useTranslation();

    const {send: loadScreenshots, data: screenshotsResponse, loading} = useApiData(settingsApi.getScreenshots,
        {notificationType: PredefinedNotifierType.FetchError});

    const [initialValues, setInitialValues] = useState<FormFields>({
        screenshots: [],
        assignment: {
            groupId: ''
        }
    });

    const {send: updateScreenshots, loading: updateLoading} = useApiData(settingsApi.updateScreenshots,
        {onSuccess: loadScreenshots, notificationType: PredefinedNotifierType.Save});

    const handleSubmit = useCallback((data: {screenshots: SessionScreenshot[], assignment: {groupId: string}}) => {
        const currentGroupId = data.assignment?.groupId;
        updateScreenshots(data.screenshots, currentGroupId);
    }, [updateScreenshots]);

    const duplicatedErrorMessage = useMemo(() => apiUtils.getValidationCodeTKey(t, ValidationCode.Duplicated), [t]);

    const validate = useCallback((formValues) => {
        const errorIndexes: {[index: number]: boolean} = {};
        const errors = {};
        const setError = (index: number) => {
            errorIndexes[index] = true;
            set(errors, `${FORM_FIELD_KEY.SCREENSHOTS}[${index}].${FORM_FIELD_KEY.NAME}`, duplicatedErrorMessage);
        };
        forEach(formValues.screenshots, (screenshot1, i: number) => {
            forEach(formValues.screenshots, (screenshot2, j: number) => {
                if (screenshot1.name === screenshot2.name && i !== j) {
                    if (!errorIndexes[i]) {
                        setError(i);
                    }
                    if (!errorIndexes[j]) {
                        setError(j);
                    }
                }
            });
        });
        return errors;
    }, [duplicatedErrorMessage]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validate,
        onSubmit: handleSubmit,
    });
    const {values: formValues} = formik;

    const maxScreenshots = useMemo(() => screenshotsResponse?.[formValues.assignment?.groupId || ACCOUNT_ASSIGNMENT].maxScreenshots,
        [formValues.assignment?.groupId, screenshotsResponse]);

    useEffect(() => {
        if (screenshotsResponse) {
            setInitialValues({
                screenshots: screenshotsResponse[formValues?.assignment.groupId || ACCOUNT_ASSIGNMENT].screenshots
                    .map((s) => ({...s, role: s.role || InviteeParticipantRole.Client})),
                assignment: {groupId: formValues?.assignment.groupId || ''}
            });
        }
    }, [formValues?.assignment.groupId, screenshotsResponse]);

    useEffect(() => {
        loadScreenshots();
    }, [loadScreenshots]);

    return (
        <div className='tw-relative'>
            <DataWrapper isLoading={loading || updateLoading} hideContentOnLoading={false}>
                <FormikProvider value={formik}>
                    <FormTemplate
                        {...formik}
                        maxScreenshots={maxScreenshots}
                        groups={groups}
                        guestEnabled={guestEnabled}
                    />
                </FormikProvider>
            </DataWrapper>
        </div>
    );
};
